import React, { useState } from 'react';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import toast from '../utils/toast';
import { exportToExcel, exportToPDF } from '../utils/exportUtils';

/**
 * Enhanced Export Buttons with "Export All" functionality
 * Supports exporting current page or all pages
 */
const ExportButtonsWithAll = ({ 
  data, 
  fileName, 
  columns, 
  pdfOptions = {}, 
  fetchAllData = null, 
  totalCount = 0,
  className = '' 
}) => {
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [showExcelDropdown, setShowExcelDropdown] = useState(false);
  const [showPDFDropdown, setShowPDFDropdown] = useState(false);

  const handleExportExcel = async (exportAll = false) => {
    try {
      setExportingExcel(true);
      let exportData = data;
      let label = 'current page';

      if (exportAll && fetchAllData) {
        label = 'all pages';
        toast.info(`Fetching all ${totalCount} records...`);
        exportData = await fetchAllData();
        
        if (!exportData || exportData.length === 0) {
          toast.error('No data available to export');
          return;
        }
      } else if (!data || data.length === 0) {
        toast.error('No data available to export');
        return;
      }

      exportToExcel(exportData, fileName, columns);
      toast.success(`${exportData.length} records exported to Excel successfully!`);
    } catch (error) {
      console.error('Export to Excel failed:', error);
      toast.error('Failed to export to Excel');
    } finally {
      setExportingExcel(false);
      setShowExcelDropdown(false);
    }
  };

  const handleExportPDF = async (exportAll = false) => {
    try {
      setExportingPDF(true);
      let exportData = data;
      let label = 'current page';

      console.log('PDF Export Debug - Starting:', { exportAll, hasData: !!data, dataLength: data?.length, hasFetchAll: !!fetchAllData });

      if (exportAll && fetchAllData) {
        label = 'all pages';
        toast.info(`Fetching all ${totalCount} records...`);
        exportData = await fetchAllData();
        
        if (!exportData || exportData.length === 0) {
          toast.error('No data available to export');
          return;
        }
      } else if (!data || data.length === 0) {
        toast.error('No data available to export');
        return;
      }

      console.log('PDF Export Debug - Data prepared:', { 
        exportDataLength: exportData?.length, 
        columns: columns?.length,
        fileName,
        pdfOptions 
      });

      // Validate data and columns before export
      if (!exportData || !Array.isArray(exportData)) {
        throw new Error('Export data is not a valid array');
      }
      
      if (!columns || !Array.isArray(columns) || columns.length === 0) {
        throw new Error('Column definitions are missing or invalid');
      }

      exportToPDF(exportData, fileName, columns, {
        title: `${fileName} Report`,
        orientation: 'landscape',
        fontSize: 8,
        ...pdfOptions
      });
      toast.success(`${exportData.length} records exported to PDF successfully!`);
    } catch (error) {
      console.error('Export to PDF failed:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        exportAll,
        hasData: !!data,
        dataLength: data?.length,
        hasColumns: !!columns,
        columnsLength: columns?.length
      });
      toast.error(`Failed to export PDF: ${error.message}`);
    } finally {
      setExportingPDF(false);
      setShowPDFDropdown(false);
    }
  };

  const exportOptions = [
    {
      label: `Export Current Page (${data?.length || 0} records)`,
      onClick: () => handleExportExcel(false),
      icon: '📊'
    },
    {
      label: `Export All Pages (${totalCount || 0} records)`,
      onClick: () => handleExportExcel(true),
      icon: '📈',
      disabled: !fetchAllData
    }
  ];

  const pdfExportOptions = [
    {
      label: `Export Current Page (${data?.length || 0} records)`,
      onClick: () => handleExportPDF(false),
      icon: '📄'
    },
    {
      label: `Export All Pages (${totalCount || 0} records)`,
      onClick: () => handleExportPDF(true),
      icon: '📋',
      disabled: !fetchAllData
    }
  ];

  return (
    <div className={`flex space-x-2 ${className}`}>
      {/* Excel Export with Dropdown */}
      <div className="relative">
        <div className="flex">
          <button
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-l-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            onClick={() => handleExportExcel(false)}
            disabled={exportingExcel}
            title="Export current page to Excel"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2 text-green-600" />
            {exportingExcel ? 'Exporting...' : 'Excel'}
          </button>
          
           {fetchAllData && (
             <button
               className="inline-flex items-center px-2 py-2 border border-l-0 border-gray-300 shadow-sm text-sm font-medium rounded-r-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
               onClick={() => setShowExcelDropdown(!showExcelDropdown)}
               disabled={exportingExcel}
               title="Export options"
             >
               <ChevronDownIcon className="h-4 w-4" />
             </button>
           )}
        </div>

        {/* Excel Export Dropdown */}
        {showExcelDropdown && fetchAllData && (
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-10 border border-gray-200">
            <div className="py-1">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                Excel Export Options
              </div>
               {exportOptions.map((option, index) => (
                 <button
                   key={index}
                   onClick={option.onClick}
                   disabled={exportingExcel || option.disabled}
                   className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center ${
                     option.disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'
                   } ${exportingExcel ? 'cursor-not-allowed' : ''}`}
                 >
                  <span className="mr-2">{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* PDF Export with Dropdown */}
      <div className="relative">
        <div className="flex">
          <button
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-l-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            onClick={() => handleExportPDF(false)}
            disabled={exportingPDF}
            title="Export current page to PDF"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2 text-red-600" />
            {exportingPDF ? 'Exporting...' : 'PDF'}
          </button>
          
           {fetchAllData && (
             <button
               className="inline-flex items-center px-2 py-2 border border-l-0 border-gray-300 shadow-sm text-sm font-medium rounded-r-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
               onClick={() => setShowPDFDropdown(!showPDFDropdown)}
               disabled={exportingPDF}
               title="Export options"
             >
               <ChevronDownIcon className="h-4 w-4" />
             </button>
           )}
        </div>

        {/* PDF Export Dropdown */}
        {showPDFDropdown && fetchAllData && (
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-10 border border-gray-200">
            <div className="py-1">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                PDF Export Options
              </div>
               {pdfExportOptions.map((option, index) => (
                 <button
                   key={index}
                   onClick={option.onClick}
                   disabled={exportingPDF || option.disabled}
                   className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center ${
                     option.disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'
                   } ${exportingPDF ? 'cursor-not-allowed' : ''}`}
                 >
                  <span className="mr-2">{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close dropdowns */}
      {(showExcelDropdown || showPDFDropdown) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setShowExcelDropdown(false);
            setShowPDFDropdown(false);
          }}
        />
      )}
    </div>
  );
};

export default ExportButtonsWithAll;
